import { z } from 'zod'

import { httpClientBanking } from '~/domains/banking/shared/infra/http-client-banking'
import { useQueryRequest } from '~/ui/hooks/utils'

import { balanceSchema } from './definitions'

const MovementTransactionSchema = z.object({
  amount: z.number(),
  description: z.string(),
  time: z.string()
})

const MovementSchema = z.object({
  amount: z.number(),
  count: z.number(),
  transactions: z.array(MovementTransactionSchema)
})

const ProductBalanceSchema = balanceSchema.pick({ interest: true, iof: true, ir: true, netAmount: true }).extend({
  iofReversion: z.number(),
  irReversion: z.number()
})

const ProductSchema = z.object({
  apy: z.string(),
  balance: ProductBalanceSchema,
  maturityDate: z.string(),
  name: z.string()
})

const ProfitsSchema = z.object({
  amount: z.number(),
  products: z.array(ProductSchema)
})

const MarketStatementSchema = z.object({
  netAmount: z.number(),
  date: z.string(),
  deposits: MovementSchema,
  profits: ProfitsSchema,
  withdrawals: MovementSchema
})

export type MarketStatement = z.infer<typeof MarketStatementSchema>

const MarketStatementResponseSchema = z.array(MarketStatementSchema)

export type MarketStatementResponse = z.infer<typeof MarketStatementResponseSchema>

async function fetchMarketStatement({
  accountId,
  from,
  to,
  productId
}: {
  accountId: string
  from: string
  to: string
  productId?: string
}) {
  const { data } = await httpClientBanking({
    url: `/investments/accounts/${accountId}/storefront/statement`,
    method: 'GET',
    params: productId ? { from, to, product_id: productId } : { from, to }
  })

  return MarketStatementResponseSchema.parse(data)
}

export function getUseFetchMarketStatementKey({
  accountId,
  from,
  to,
  productId
}: {
  accountId: string
  from: string
  to: string
  productId?: string
}) {
  return ['useFetchMarketStatement', accountId, from, to, productId] as const
}

export function useFetchMarketStatement({
  accountId,
  from,
  to,
  productId,
  options
}: {
  accountId: string
  from: string
  to: string
  productId?: string
  options?: { suspense?: boolean }
}) {
  return useQueryRequest<MarketStatementResponse, unknown, true>(
    [...getUseFetchMarketStatementKey({ accountId, from, to, productId })],
    () => fetchMarketStatement({ accountId, from, to, productId }),
    {
      suspense: true,
      ...options
    }
  )
}
